/** Auth & profile endpoints (CONTRACT §2). */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";
import type {
  AuthResponse,
  AuthSession,
  ChangePasswordPayload,
  ConfirmPasswordResetPayload,
  LoginPayload,
  OkMessage,
  Profile,
  ProfileUpdatePayload,
  RequestPasswordResetPayload,
  User,
} from "./types";

export async function login(payload: LoginPayload): Promise<AuthResponse> {
  const { data } = await api.post<AuthResponse>("/auth/login", payload);
  return data;
}

/** Change the password (forced first-login OR voluntary). Returns a NEW full token + user. */
export async function changePassword(
  payload: ChangePasswordPayload,
): Promise<AuthResponse> {
  const { data } = await api.post<AuthResponse>("/auth/change-password", payload);
  return data;
}

/** Always succeeds — never reveals whether the email exists. */
export async function requestPasswordReset(
  payload: RequestPasswordResetPayload,
): Promise<OkMessage> {
  const { data } = await api.post<OkMessage>("/auth/reset-password/request", payload);
  return data;
}

export async function confirmPasswordReset(
  payload: ConfirmPasswordResetPayload,
): Promise<OkMessage> {
  const { data } = await api.post<OkMessage>("/auth/reset-password/confirm", payload);
  return data;
}

export async function fetchMe(): Promise<User> {
  const { data } = await api.get<User>("/auth/me");
  return data;
}

export function useProfile(enabled = true) {
  return useQuery({
    queryKey: ["profile"],
    queryFn: async (): Promise<Profile> => {
      const { data } = await api.get<Profile>("/profile");
      return data;
    },
    enabled,
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: ProfileUpdatePayload): Promise<Profile> => {
      const { data } = await api.patch<Profile>("/profile", payload);
      return data;
    },
    onSuccess: (data) => {
      qc.setQueryData(["profile"], data);
    },
  });
}

// ── Session management (v2.1 Phase 15) ──────────────────────────────────────
export function useSessions() {
  return useQuery({
    queryKey: ["auth-sessions"],
    queryFn: async (): Promise<AuthSession[]> => {
      const { data } = await api.get<AuthSession[]>("/auth/sessions");
      return data;
    },
  });
}

export function useRevokeSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await api.delete(`/auth/sessions/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["auth-sessions"] }),
  });
}

export function useRevokeOtherSessions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<void> => {
      await api.post("/auth/sessions/revoke-all", { keep_current: true });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["auth-sessions"] }),
  });
}

/** Best-effort server-side logout: revoke THIS device's refresh session so its token can't be
 *  refreshed again. Never throws (logout must always proceed to clear local state). */
export async function logoutServer(): Promise<void> {
  try {
    await api.post("/auth/logout", {});
  } catch {
    /* ignore — local logout still clears tokens */
  }
}
